/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import React from 'react';
import { decorate, action } from 'mobx';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Menu, Icon, Image } from 'semantic-ui-react';

import { createLink } from '../helpers/routing';
import { displayError } from '../helpers/notification';
import { branding, versionAndDate } from '../helpers/settings';
import ChatBot from './ChatBot';
import Declaimer from './Declaimer';

// expected props
// - userStore (via injection)
class MainLayout extends React.Component {
  constructor() {
    super();
    this.state = {
      sideMenuWidth: 84,
      topMenuHeight: 50,
    };
  }

  windowSizeChanged = () => {
    const sideMenu = document.getElementById('main-layout-side-menu');
    const topMenu = document.getElementById('main-layout-top-menu');
    if (sideMenu && topMenu) {
      const sideMenuWidth = sideMenu.offsetWidth;
      const topMenuHeight = topMenu.offsetHeight;
      this.setState({
        sideMenuWidth,
        topMenuHeight,
      });
    }
  };

  componentWillUnmount = () => {
    window.removeEventListener('resize', this.windowSizeChanged);
  };

  componentDidMount() {
    this.windowSizeChanged();
    window.addEventListener('resize', this.windowSizeChanged);
  }

  goto = pathname => () => {
    const location = this.props.location;
    const link = createLink({
      location,
      pathname,
    });

    this.props.history.push(link);
  };

  handleLogout = async event => {
    event.preventDefault();
    event.stopPropagation();

    try {
      await this.props.authentication.logout();
    } catch (error) {
      displayError(error);
    }
  };

  getMenuItems() {
    return this.props.menuItems || [];
  }

  render() {
    const currentUser = this.props.userStore.user;
    const displayName = currentUser ? currentUser.displayName : 'Not Logged In';
    const pathname = _.get(this.props.location, 'pathname', '');
    const is = value => _.startsWith(pathname, value);

    const itemsArr = this.getMenuItems();
    return [
      <Menu id="main-layout-side-menu" vertical inverted fixed="left" icon="labeled" key="ml1">
        {/* <div className='flow-menu-items'> */}
        <Menu.Item name=" " style={{ height: `${this.state.topMenuHeight}px` }} />
        {_.map(itemsArr, (item, index) => {
          const show = (_.isFunction(item.shouldShow) && item.shouldShow()) || item.shouldShow;
          return (
            show &&
            (item.body ? (
              item.body
            ) : (
              <Menu.Item key={index} active={is(item.url)} onClick={is(item.url) ? undefined : this.goto(item.url)}>
                <Icon name={item.icon} size="mini" />
                <span className="fs-7">{item.title}</span>
              </Menu.Item>
            ))
          );
        })}
        {/* </div> */}
        {/* <Menu.Item id='side-race-logo'>
          <img src={this.props.assets.images.poweredByRACESq2} />
        </Menu.Item> */}
      </Menu>,

      <Menu id="main-layout-top-menu" inverted color="black" fixed="top" className="box-shadow zindex-1500" key="ml2">
        <Menu.Item className="static-item" style={{ height: '50px', verticalAlign: 'middle' }}>
          <div className="logo-container">
            <Image src={this.props.assets.images.ardcLogo} className="mr1 top-menu-main-logo" />
          </div>
          <span style={{ marginLeft: '20px' }}>{branding.main.title}</span>
          <span style={{ marginLeft: '20px' }}>{versionAndDate}</span>
        </Menu.Item>
        <Menu.Menu position="right">
          {/* <Menu.Item>
            <Icon name="info" />
            <a className='on-top-menu-a' href="/static/files/race_guide.pdf" download="RACEHub User Guide.pdf">
              Download User Guide
            </a>
          </Menu.Item> */}
          <Menu.Item>
            <Icon name="user" /> {displayName}
          </Menu.Item>
          <Menu.Item name="logout" onClick={this.handleLogout} icon="sign-out" />
        </Menu.Menu>
      </Menu>,
      <div
        className="mainLayout fit animated fadeIn"
        style={{
          paddingTop: `${this.state.topMenuHeight}px`,
          paddingLeft: `${this.state.sideMenuWidth}px`,
          // paddingBottom: '100px',
        }}
        key="ml3"
      >
        {process.env.REACT_APP_CHATBOT_ENABLE === 'true' && <ChatBot />}
        {this.props.children}
        <Declaimer />
      </div>,

      // Add this section to show icon on the bottom right of page
      // <div className="fixed-position-icon">
      //   <Icon name="info" />
      // </div>,
    ];
  }
}

// see https://medium.com/@mweststrate/mobx-4-better-simpler-faster-smaller-c1fbc08008da
decorate(MainLayout, {
  handleLogout: action,
});

export default inject('authentication', 'userStore', 'assets')(withRouter(observer(MainLayout)));
